import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ExternalLink, Download, FileCheck2, AlertOctagon, ShieldCheck, CircleDot, Copy, Ticket } from "lucide-react";
import ScanHistory from "@/components/ScanHistory";

const VERDICT_STYLE = {
  a_couvrir: "tag-orange",
  couvert: "tag-green",
  expire: "tag-red",
  non_conforme: "tag-red",
};

export default function Dashboard() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [claims, setClaims] = useState([]);
  const [sites, setSites] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");

  const loadAll = async () => {
    const [m, s, c, p] = await Promise.all([
      api.get("/dashboard/metrics"),
      api.get("/sites"),
      api.get("/claims"),
      api.get("/proofs"),
    ]);
    setMetrics(m.data); setSites(s.data); setClaims(c.data); setProofs(p.data);
    if (!selectedSite && s.data[0]) setSelectedSite(s.data[0].id);
  };
  useEffect(() => { loadAll(); }, []);

  const generateDossier = async () => {
    if (!selectedSite) { toast.error(t("dash.no_site")); return; }
    toast.loading("Génération du dossier…", { id: "dossier" });
    try {
      const r = await api.get(`/dossier/${selectedSite}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "dossier-controle.pdf"; a.click();
      toast.success("Dossier prêt", { id: "dossier" });
    } catch { toast.error("Erreur", { id: "dossier" }); }
  };

  const updateVerdict = async (claim, verdict) => {
    await api.patch(`/claims/${claim.id}`, { verdict });
    toast.success("Verdict mis à jour");
    loadAll();
  };

  const attachProof = async (claim, proofId) => {
    const pids = [...(claim.proof_ids || []), proofId];
    await api.patch(`/claims/${claim.id}`, { proof_ids: pids });
    toast.success("Preuve attachée");
    loadAll();
  };

  const visibleClaims = selectedSite ? claims.filter(c => c.site_id === selectedSite) : claims;

  return (
    <div className="space-y-8 md:space-y-10" data-testid="dashboard">
      {Number(user?.prepaid_scans || 0) > 0 && (
        <div className="hairline p-5 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-emerald-50 border-emerald-200" data-testid="prepaid-banner">
          <div className="flex items-start gap-3">
            <Ticket className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div>
              <p className="font-serif text-base text-emerald-900">
                {user.prepaid_scans} scan{user.prepaid_scans > 1 ? "s" : ""} prépayé{user.prepaid_scans > 1 ? "s" : ""} disponible{user.prepaid_scans > 1 ? "s" : ""}
              </p>
              <p className="text-sm text-emerald-800/80">
                Lancez un scan complet gratuitement depuis votre crédit prépayé — il sera automatiquement déduit.
              </p>
            </div>
          </div>
          <Button asChild className="rounded-lg h-10 shrink-0" data-testid="prepaid-cta">
            <a href="/app/scan">Lancer un scan</a>
          </Button>
        </div>
      )}
      {/* Metrics */}
      <div>
        <p className="eyebrow mb-2">Vue synthétique</p>
        <h1 className="text-3xl sm:text-4xl font-serif tracking-tight mb-4 sm:mb-6">{t("dash.title")}</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <MetricCell testid="metric-claims" label={t("dash.metrics.claims")} value={metrics?.total_claims ?? "—"} Icon={FileCheck2} />
          <MetricCell testid="metric-proofs" label={t("dash.metrics.proofs")} value={metrics?.proofs_count ?? "—"} Icon={ShieldCheck} />
          <MetricCell testid="metric-alerts" label={t("dash.metrics.alerts")} value={metrics?.unread_alerts ?? "—"} Icon={AlertOctagon} accent="red" />
          <MetricCell testid="metric-quota" label={t("dash.metrics.quota")} value={`${metrics?.quota_used ?? 0}/${metrics?.quota_max ?? 100}`} Icon={CircleDot} />
        </div>
      </div>

      {/* Site selector + dossier */}
      <div className="hairline p-6 rounded-lg flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex-1 max-w-md">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Site actif</Label>
            {sites.length > 0 ? (
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger className="rounded-lg h-11 border-border" data-testid="site-selector">
                  <SelectValue placeholder="Sélectionner un site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.domain} {s.verified ? "✓" : "· non vérifié"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground italic">{t("dash.no_site")}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <SiteDialog onCreated={loadAll} />
          <DedupButton siteId={selectedSite} />
          <Button onClick={generateDossier} disabled={!selectedSite} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11" data-testid="generate-dossier-btn">
            <Download className="w-4 h-4 mr-2" /> {t("dash.dossier")}
          </Button>
        </div>
      </div>

      {/* Claims registry */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="eyebrow mb-1">Registre</p>
            <h2 className="text-2xl font-serif">{visibleClaims.length} allégation{visibleClaims.length > 1 ? "s" : ""}</h2>
          </div>
          <ClaimDialog sites={sites} onCreated={loadAll} />
        </div>
        <div className="hairline overflow-hidden">
          {visibleClaims.length === 0 && (
            <p className="p-10 text-center text-sm text-muted-foreground">Aucune allégation. Lancez un scan site internet ou ajoutez-en manuellement.</p>
          )}
          {visibleClaims.map((c, i) => (
            <div key={c.id} className={`p-5 grid md:grid-cols-[1fr_auto_auto] gap-4 items-start ${i > 0 ? "border-t border-border" : ""}`} data-testid={`claim-${c.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`px-1.5 h-5 text-[10px] font-mono flex items-center rounded-lg ${c.severity === "red" ? "tag-red" : c.severity === "green" ? "tag-green" : "tag-orange"}`}>
                    {c.severity.toUpperCase()}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground">{c.category}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">v{c.version || 1}</span>
                </div>
                <p className="font-serif text-base leading-snug mb-2">« {c.text} »</p>
                <a href={c.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-mono">
                  <ExternalLink className="w-3 h-3" /> {c.url}
                </a>
                {c.reformulation && (
                  <p className="mt-3 text-sm text-[hsl(var(--status-green))]">↪ « {c.reformulation} »</p>
                )}
                {c.proof_ids?.length > 0 && (
                  <p className="mt-2 text-xs font-mono text-muted-foreground">Preuves&nbsp;: {c.proof_ids.length} attachée{c.proof_ids.length > 1 ? "s" : ""}</p>
                )}
              </div>
              <Select value={c.verdict || "a_couvrir"} onValueChange={(v) => updateVerdict(c, v)}>
                <SelectTrigger className={`rounded-lg h-8 text-xs w-36 border ${VERDICT_STYLE[c.verdict || "a_couvrir"]}`} data-testid={`verdict-${c.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_couvrir">{t("verdicts.a_couvrir")}</SelectItem>
                  <SelectItem value="couvert">{t("verdicts.couvert")}</SelectItem>
                  <SelectItem value="expire">{t("verdicts.expire")}</SelectItem>
                  <SelectItem value="non_conforme">{t("verdicts.non_conforme")}</SelectItem>
                </SelectContent>
              </Select>
              {proofs.length > 0 && (
                <Select onValueChange={(v) => attachProof(c, v)}>
                  <SelectTrigger className="rounded-lg h-8 text-xs w-40 border-border" data-testid={`attach-${c.id}`}>
                    <SelectValue placeholder="Attacher preuve" />
                  </SelectTrigger>
                  <SelectContent>
                    {proofs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <ScanHistory limit={25} title="Historique des scans" />
      </section>
    </div>
  );
}

function MetricCell({ label, value, Icon, accent, testid }) {
  return (
    <div className="hairline card-lift p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-5">
        <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === "red" ? "bg-[hsl(var(--status-red))]/10 text-[hsl(var(--status-red))]" : "bg-accent text-primary"}`}><Icon className="w-4 h-4" /></span>
      </div>
      <p className={`text-3xl sm:text-4xl font-serif tracking-tight ${accent === "red" ? "text-[hsl(var(--status-red))]" : "text-primary"}`}>{value}</p>
    </div>
  );
}

function DedupButton({ siteId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ threshold: "0.85" });
      if (siteId) params.set("site_id", siteId);
      const { data: d } = await api.post(`/dedup/analyze?${params.toString()}`);
      setData(d);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'analyse");
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button onClick={run} disabled={loading} variant="outline" className="rounded-lg h-11" data-testid="dedup-btn">
        <Copy className="w-4 h-4 mr-2" /> {loading ? "Analyse…" : "Détecter doublons"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dedup-dialog">
          <DialogHeader>
            <DialogTitle>Analyse de doublons</DialogTitle>
          </DialogHeader>
          {data && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="hairline p-3 rounded-lg text-center">
                  <p className="text-[10px] uppercase text-muted-foreground">Total</p>
                  <p className="text-2xl font-serif text-primary">{data.total_claims}</p>
                </div>
                <div className="hairline p-3 rounded-lg text-center">
                  <p className="text-[10px] uppercase text-muted-foreground">Uniques</p>
                  <p className="text-2xl font-serif text-secondary">{data.unique_claims}</p>
                </div>
                <div className="hairline p-3 rounded-lg text-center">
                  <p className="text-[10px] uppercase text-muted-foreground">Clusters</p>
                  <p className="text-2xl font-serif text-primary">{data.clusters.length}</p>
                </div>
              </div>
              {data.clusters.length === 0 ? (
                <p className="text-sm text-muted-foreground italic p-4 text-center">Aucun doublon détecté ✨</p>
              ) : (
                data.clusters.map((c, i) => (
                  <div key={c.representative?.id || c.representative?.text?.slice(0, 40) || `cluster-${i}`} className="hairline p-4 rounded-lg" data-testid={`dedup-cluster-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-primary">Cluster #{i + 1}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{c.count} allégations · {(c.avg_score * 100).toFixed(0)}%</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">« {c.representative.text} »</p>
                    <ul className="mt-2 space-y-1 pl-4 border-l-2 border-secondary/40">
                      {c.duplicates.map((d) => (
                        <li key={d.id || d.text?.slice(0, 40)} className="text-xs text-muted-foreground italic">« {d.text} »</li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function SiteDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [method, setMethod] = useState("dns_txt");

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/sites", { domain });
      setCreated(data);
      toast.success("Site ajouté");
    } catch (e) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };
  const verify = async () => {
    try {
      await api.post(`/sites/${created.id}/verify`, null, { params: { method } });
      toast.success("Propriété vérifiée");
      setOpen(false); setCreated(null); setDomain("");
      onCreated();
    } catch { toast.error("Erreur"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-lg h-11 border-border" data-testid="add-site-btn"><Plus className="w-4 h-4 mr-1" /> Site</Button>
      </DialogTrigger>
      <DialogContent className="rounded-lg max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Ajouter un site</DialogTitle></DialogHeader>
        {!created ? (
          <div className="space-y-4">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Domaine</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="exemple.fr" className="rounded-lg h-11" data-testid="site-domain-input" />
            <DialogFooter>
              <Button onClick={create} disabled={!domain || saving} className="rounded-lg bg-primary text-primary-foreground" data-testid="site-create-btn">Créer</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Choisissez une méthode de vérification pour <b>{created.domain}</b>&nbsp;:</p>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-lg" data-testid="verify-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dns_txt">Enregistrement DNS TXT</SelectItem>
                <SelectItem value="meta_html">Balise meta HTML</SelectItem>
                <SelectItem value="well_known">Fichier /.well-known</SelectItem>
              </SelectContent>
            </Select>
            <div className="bg-muted p-4 rounded-lg border border-border">
              <p className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Token à publier</p>
              <p className="font-mono text-sm break-all select-all">empco-verify={created.verification_token}</p>
            </div>
            <DialogFooter>
              <Button onClick={verify} className="rounded-lg bg-primary text-primary-foreground" data-testid="site-verify-btn">Vérifier maintenant</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ClaimDialog({ sites, onCreated }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [siteId, setSiteId] = useState("");
  const [severity, setSeverity] = useState("orange");
  const submit = async () => {
    try {
      await api.post("/claims", { site_id: siteId, text, url, severity });
      toast.success("Allégation ajoutée");
      setOpen(false); setText(""); setUrl(""); setSiteId("");
      onCreated();
    } catch { toast.error("Erreur"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-lg border-border" data-testid="add-claim-btn"><Plus className="w-4 h-4 mr-1" /> Allégation</Button>
      </DialogTrigger>
      <DialogContent className="rounded-lg max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Ajouter une allégation</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Select value={siteId} onValueChange={setSiteId}>
            <SelectTrigger className="rounded-lg" data-testid="claim-site-select"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>{sites.map(s => <SelectItem key={s.id} value={s.id}>{s.domain}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Texte exact de l'allégation" className="rounded-lg" data-testid="claim-text-input" />
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="URL de la page" className="rounded-lg h-11" data-testid="claim-url-input" />
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="rounded-lg" data-testid="claim-severity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="red">Red</SelectItem>
              <SelectItem value="orange">Orange</SelectItem>
              <SelectItem value="green">Green</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={submit} disabled={!siteId || !text || !url} className="rounded-lg bg-primary text-primary-foreground" data-testid="claim-submit-btn">Ajouter</Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
